import { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

interface BlurImageProps {
  src: string;
  alt: string;
  className?: string;
  placeholderClassName?: string;
}

export const BlurImage = ({ src, alt, className, placeholderClassName }: BlurImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    // Reset state when src changes
    setIsLoaded(false);
    setHasError(false);
    
    // Check if image is already cached
    if (imgRef.current?.complete && imgRef.current?.naturalHeight !== 0) {
      setIsLoaded(true);
    }
  }, [src]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    setHasError(true);
  };

  if (hasError) {
    return (
      <div className={cn(
        "bg-gradient-to-br from-primary/10 to-accent/10",
        className
      )} />
    );
  }

  return (
    <div className={cn("relative overflow-hidden", className)}>
      {/* Blurred placeholder */}
      <div 
        className={cn(
          "absolute inset-0 bg-gradient-to-br from-muted to-muted/50",
          "transition-opacity duration-700 ease-out",
          isLoaded ? "opacity-0" : "opacity-100",
          placeholderClassName
        )}
      >
        {/* Shimmer overlay */}
        <div 
          className={cn(
            "absolute inset-0",
            !isLoaded && "before:absolute before:inset-0 before:-translate-x-full before:animate-shimmer before:bg-gradient-to-r before:from-transparent before:via-muted-foreground/10 before:to-transparent"
          )}
        />
      </div>
      
      {/* Actual image */}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        loading="lazy"
        className={cn(
          "w-full h-full object-cover",
          "transition-all duration-700 ease-out",
          isLoaded 
            ? "opacity-100 blur-0 scale-100" 
            : "opacity-0 blur-sm scale-105"
        )}
      />
    </div>
  );
};
